import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Library, Upload, Loader2, FileText, Image as ImageIcon, Download, Trash2,
    Music, FileSpreadsheet, Presentation, Filter, Edit3, X,
} from "lucide-react";
import { formatReceivedAt } from "../../lib/dateFormat";

/**
 * V166 — Study Materials Library.
 *
 * Party-scoped: tutor sees Upload button + delete/rename affordances,
 * parent only sees the list and Download links. Filtering by category
 * happens client-side once loaded (categories arrive from the counts map).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const CATEGORY_LABELS = {
    notes: "Notes",
    worksheet: "Worksheets",
    reference: "Reference",
    recording: "Recordings",
    other: "Other",
};

const MATERIAL_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.mp3,.m4a,.wav,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,audio/*";
const MATERIAL_MAX_MB = 25;

export default function StudyMaterialsPanel({ party, token }) {
    const isTutor = party === "tutor";
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [editing, setEditing] = useState(null); // material record being renamed

    const reload = useCallback(async () => {
        try {
            const r = await publicApi.get(`/portal/${party}/${token}/materials?_=${Date.now()}`);
            setItems(r.data?.items || []);
            setCounts(r.data?.counts || {});
        } catch (_e) { /* transient */ }
        finally { setLoading(false); }
    }, [party, token]);

    useEffect(() => { reload(); }, [reload]);

    const uploadFile = async ({ file, title, category, description }) => {
        if (!file) return;
        if (file.size > MATERIAL_MAX_MB * 1024 * 1024) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${MATERIAL_MAX_MB} MB.`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (title) fd.append("title", title);
            if (category) fd.append("category", category);
            if (description) fd.append("description", description);
            await publicApi.post(`/portal/tutor/${token}/materials`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Material uploaded");
            setShowUpload(false);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const removeMaterial = async (id) => {
        if (!confirm("Remove this material? Parent view will no longer see it.")) return;
        try {
            await publicApi.delete(`/portal/tutor/${token}/materials/${id}`);
            toast.success("Removed");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove");
        }
    };

    const saveEdit = async (id, patch) => {
        try {
            await publicApi.patch(`/portal/tutor/${token}/materials/${id}`, patch);
            toast.success("Updated");
            setEditing(null);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    const visible = filter === "all" ? items : items.filter((m) => (m.category || "other") === filter);
    const activeCategories = Object.entries(counts).filter(([, n]) => n > 0);

    return (
        <section data-testid={`portal-materials-${party}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-1.5">
                <Library className="w-3 h-3" /> Study Materials
                {items.length > 0 && <span className="text-slate-400 font-normal">· {items.length}</span>}
                {isTutor && (
                    <button
                        type="button"
                        onClick={() => setShowUpload(true)}
                        data-testid="materials-upload-btn"
                        data-cm-primary
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-cyan-600 text-white px-3 py-1.5 rounded-full hover:bg-cyan-700"
                    >
                        <Upload className="w-3.5 h-3.5" /> Upload
                    </button>
                )}
            </div>

            {/* Filter pills — only show when we have >1 category with content */}
            {activeCategories.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto pb-2" data-testid="materials-filter-tabs">
                    <FilterPill label="All" count={items.length} active={filter === "all"} onClick={() => setFilter("all")} />
                    {activeCategories.map(([cat, n]) => (
                        <FilterPill
                            key={cat}
                            label={CATEGORY_LABELS[cat] || cat}
                            count={n}
                            active={filter === cat}
                            onClick={() => setFilter(cat)}
                            testid={`materials-filter-${cat}`}
                        />
                    ))}
                </div>
            )}

            {loading ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-slate-400 text-xs">
                    <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                </div>
            ) : visible.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center text-xs text-slate-500" data-testid="materials-empty">
                    {isTutor
                        ? "No materials yet. Tap Upload to share notes, worksheets or reference files."
                        : "Your tutor hasn't shared any materials yet. Check back soon."}
                </div>
            ) : (
                <ul className="space-y-2" data-testid="materials-list">
                    {visible.map((m) => (
                        <MaterialRow
                            key={m.id}
                            material={m}
                            party={party}
                            token={token}
                            isTutor={isTutor}
                            onRemove={() => removeMaterial(m.id)}
                            onEdit={() => setEditing(m)}
                        />
                    ))}
                </ul>
            )}

            {showUpload && (
                <UploadDialog
                    uploading={uploading}
                    onCancel={() => setShowUpload(false)}
                    onSubmit={uploadFile}
                />
            )}
            {editing && (
                <EditDialog
                    material={editing}
                    onCancel={() => setEditing(null)}
                    onSave={(patch) => saveEdit(editing.id, patch)}
                />
            )}
        </section>
    );
}

// ---------------------------------------------------------- Row

function MaterialRow({ material, party, token, isTutor, onRemove, onEdit }) {
    const dl = `${API}/portal/${party}/${token}/materials/${material.id}/download`;
    const isImage = (material.content_type || "").startsWith("image/");
    const isAudio = (material.content_type || "").startsWith("audio/");
    const isPdf = material.content_type === "application/pdf";
    const isSheet = material.content_type?.includes("spreadsheet") || material.content_type?.includes("excel");
    const isSlide = material.content_type?.includes("presentation") || material.content_type?.includes("powerpoint");

    let Icon = FileText;
    let iconTone = "text-slate-600 bg-slate-100";
    if (isImage) { Icon = ImageIcon; iconTone = "text-emerald-700 bg-emerald-100"; }
    else if (isPdf) { Icon = FileText; iconTone = "text-rose-700 bg-rose-100"; }
    else if (isAudio) { Icon = Music; iconTone = "text-violet-700 bg-violet-100"; }
    else if (isSheet) { Icon = FileSpreadsheet; iconTone = "text-emerald-700 bg-emerald-50"; }
    else if (isSlide) { Icon = Presentation; iconTone = "text-orange-700 bg-orange-100"; }

    return (
        <li>
            <div
                className="bg-white border border-slate-200 rounded-2xl p-3 flex items-start gap-3"
                data-testid={`material-row-${material.id}`}
            >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconTone}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="text-sm font-heading font-bold text-[#0F172A] truncate">{material.title}</div>
                            <div className="text-[11px] text-slate-500">
                                <span className="uppercase tracking-wider font-semibold">{CATEGORY_LABELS[material.category] || material.category}</span>
                                <span className="mx-1">·</span>
                                {fmtSize(material.size)}
                                <span className="mx-1">·</span>
                                {formatReceivedAt(material.created_at)}
                            </div>
                        </div>
                        {isTutor && (
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={onEdit}
                                    data-testid={`material-edit-${material.id}`}
                                    data-cm-compact
                                    aria-label="Rename"
                                    className="p-2 text-slate-600 hover:text-cyan-700 hover:bg-slate-50 rounded"
                                >
                                    <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={onRemove}
                                    data-testid={`material-remove-${material.id}`}
                                    data-cm-compact
                                    aria-label="Remove"
                                    className="p-2 text-rose-600 hover:bg-rose-50 rounded"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    {material.description && (
                        <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{material.description}</p>
                    )}
                    <a
                        href={dl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`material-download-${material.id}`}
                        data-cm-primary
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-cyan-600 text-white px-3 py-1.5 rounded-lg hover:bg-cyan-700"
                    >
                        <Download className="w-3.5 h-3.5" /> Open · {(material.original_filename || "file")}
                    </a>
                </div>
            </div>
        </li>
    );
}

function FilterPill({ label, count, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            data-active={active ? "true" : "false"}
            data-cm-compact
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors min-h-[32px] ${
                active
                    ? "bg-[#0F172A] text-white border-[#0F172A]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-cyan-300"
            }`}
        >
            <Filter className="w-3 h-3" /> {label}
            <span className={`ml-0.5 text-[10px] ${active ? "opacity-70" : "text-slate-500"}`}>{count}</span>
        </button>
    );
}

// ---------------------------------------------------------- Upload dialog

function UploadDialog({ uploading, onCancel, onSubmit }) {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("notes");
    const [description, setDescription] = useState("");
    const fileRef = useRef(null);

    const submit = (e) => {
        e.preventDefault();
        if (!file || uploading) return;
        onSubmit({
            file,
            title: title.trim() || null,
            category,
            description: description.trim() || null,
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="materials-upload-dialog"
        >
            <form onSubmit={submit} className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">Upload Material</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A]">Share with student</div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={uploading}
                        data-testid="materials-upload-close"
                        data-cm-compact
                        aria-label="Cancel"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                    <Field label="File *">
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept={MATERIAL_ACCEPT}
                                className="hidden"
                                data-testid="materials-upload-file-input"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                data-testid="materials-upload-pick"
                                className="w-full inline-flex items-center gap-2 justify-center bg-slate-50 border-2 border-dashed border-slate-300 text-slate-700 hover:border-cyan-400 hover:text-cyan-800 rounded-xl py-4 text-sm font-semibold"
                            >
                                <Upload className="w-5 h-5" />
                                {file ? `Change file · ${file.name}` : "Choose file (PDF, image, DOC, PPT, audio · 25 MB max)"}
                            </button>
                            {file && (
                                <div className="text-[11px] text-slate-500 mt-1 truncate">{file.name} · {fmtSize(file.size)}</div>
                            )}
                        </div>
                    </Field>
                    <Field label="Title">
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            data-testid="materials-upload-title"
                            placeholder="Defaults to the filename"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </Field>
                    <Field label="Category">
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            data-testid="materials-upload-category"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none bg-white"
                        >
                            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                <option key={v} value={v}>{l}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Description">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="materials-upload-description"
                            rows={3}
                            placeholder="Optional — what's inside, when to use it, etc."
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                        />
                    </Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-5 py-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={uploading}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!file || uploading}
                        data-testid="materials-upload-submit"
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Upload
                    </button>
                </div>
            </form>
        </div>
    );
}

// ---------------------------------------------------------- Edit dialog

function EditDialog({ material, onCancel, onSave }) {
    const [title, setTitle] = useState(material.title || "");
    const [category, setCategory] = useState(material.category || "notes");
    const [description, setDescription] = useState(material.description || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setSaving(true);
        try {
            await onSave({ title: title.trim(), category, description: description.trim() });
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="materials-edit-dialog"
        >
            <form onSubmit={submit} className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">Edit Material</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A] truncate">{material.original_filename}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={saving}
                        data-testid="materials-edit-close"
                        data-cm-compact
                        aria-label="Cancel"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                    <Field label="Title *">
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            data-testid="materials-edit-title"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </Field>
                    <Field label="Category">
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            data-testid="materials-edit-category"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none bg-white"
                        >
                            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                <option key={v} value={v}>{l}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Description">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="materials-edit-description"
                            rows={3}
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                        />
                    </Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-5 py-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={saving}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!title.trim() || saving}
                        data-testid="materials-edit-submit"
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                        Save
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">{label}</div>
            {children}
        </label>
    );
}

function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
